import { cloneDeep, isEmpty } from "lodash";
import * as Yup from "yup";
import type {
	ContactsItems,
	Descriptions,
	FaqsItems,
	FeaturesItems,
	Legal,
	LinkedResourcesItems,
	Logo,
	ProductListing,
	QuickStartConfiguration,
	SearchAliasesItems,
	Support,
} from "../types/generated-graphql";
import Env from "../utils/env";
import { email, phoneNumber, requiredString } from "../utils/yupValidation";
import { CompanyProfileValidationSchema } from "./companyProfile";
import type { ICompanyDetails } from "./pfsContants";
import { TabNames } from "./product";
import {
	MAXIMUM_10_CONTACTS,
	PRODUCT_FAQ_ANSWER_MAXLENGTH,
	PRODUCT_FAQ_QUESTION_MAXLENGTH,
	PRODUCT_FEATURES_DESCRIPTION_MAXLENGTH,
	PRODUCT_FEATURES_TITLE_MAXLENGTH,
	PRODUCT_GENERAL_LONG_DESCRIPTION_MAXLENGTH,
	PRODUCT_GENERAL_LONG_DESCRIPTION_MINLENGTH,
	PRODUCT_GENERAL_SHORT_DESCRIPTION_MAXLENGTH,
	PRODUCT_GENERAL_SHORT_DESCRIPTION_MINLENGTH,
	PRODUCT_LINKED_RESOURCES_DESCRIPTION_MAXLENGTH,
	PRODUCT_LINKED_RESOURCES_TITLE_MAXLENGTH,
	PRODUCT_LINKED_RESOURCES_URL_MAXLENGTH,
	PRODUCT_NAME_MAXLENGTH,
	PRODUCT_QUICK_START_MAXLENGTH,
	PRODUCT_SUPPORT_MAXLENGTH,
	RED_HAT_NAMING_GUIDELINE_ERROR,
	REQUIRED_MAX_CHAR_LENGTH,
	REQUIRED_MESSAGE,
	UNICODE_URL_REGEX,
	UNIQUE_EMAIL,
	VALID_EMAIL,
	VALID_URL,
} from "./validation";

// Extend Yup types
declare module "yup" {
	interface StringSchema {
		unicodeUrl(message?: string): this;
	}
	interface ObjectSchema<T> {
		uniqueProperty(propertyName: string, message: string): this;
	}
}

// ============================================================================
// FORM FIELD KEY CONSTANTS
// Centralized constants for all form field names - use these instead of hardcoded strings
// ============================================================================

// General Details Form
export const GeneralDetailsFormKeys = {
	name: "name",
	logo: "logo",
	descriptions: "descriptions",
} as const;

export const LogoFormKeys = {
	url: "url",
} as const satisfies Record<string, keyof Logo>;

export const DescriptionsFormKeys = {
	short: "short",
	long: "long",
} as const satisfies Record<string, keyof Descriptions>;

// Features Form
export const FeaturesFormKeys = {
	features: "features",
} as const;

export const FeatureItemKeys = {
	title: "title",
	description: "description",
} as const satisfies Record<string, keyof FeaturesItems>;

// Quick Start Form
export const QuickStartFormKeys = {
	quick_start_configuration: "quick_start_configuration",
} as const;

export const QuickStartConfigKeys = {
	instructions: "instructions",
	default_deploy_instructions: "default_deploy_instructions",
} as const satisfies Record<string, keyof QuickStartConfiguration>;

// Linked Resources Form
export const LinkedResourcesFormKeys = {
	linked_resources: "linked_resources",
} as const;

export const LinkedResourceItemKeys = {
	type: "type",
	title: "title",
	description: "description",
	url: "url",
	thumbnail_url: "thumbnail_url",
} as const satisfies Record<string, keyof LinkedResourcesItems>;

// FAQ Form
export const FAQFormKeys = {
	faqs: "faqs",
} as const;

export const FAQItemKeys = {
	question: "question",
	answer: "answer",
} as const satisfies Record<string, keyof FaqsItems>;

// Support Form
export const SupportFormKeys = {
	support: "support",
} as const;

export const SupportItemKeys = {
	description: "description",
	url: "url",
	phone_number: "phone_number",
	email_address: "email_address",
} as const satisfies Record<string, keyof Support>;

// Legal Form
export const LegalFormKeys = {
	legal: "legal",
} as const;

export const LegalItemKeys = {
	license_agreement_url: "license_agreement_url",
	privacy_policy_url: "privacy_policy_url",
} as const satisfies Record<string, keyof Legal>;

// SEO Form
export const SEOFormKeys = {
	functional_categories: "functional_categories",
	search_aliases: "search_aliases",
} as const;

export const SearchAliasItemKeys = {
	key: "key",
	value: "value",
} as const satisfies Record<string, keyof SearchAliasesItems>;

// Contacts Form
export const ContactsFormKeys = {
	contacts: "contacts",
} as const;

export const ContactItemKeys = {
	email_address: "email_address",
	type: "type",
} as const satisfies Record<string, keyof ContactsItems>;

// ProductListing Main validation keys
export const ProductFormKeys = {
	id: "_id",
} as const satisfies Record<string, keyof ProductListing>;

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

export const isNameValid = (text: string | null | undefined) => {
	if (!text) {
		return true;
	}

	const startPattern = /^[^a-z0-9]*red[^a-z0-9]*hat/i;
	const isSubstringPattern = /red[^a-z0-9]*hat/i;
	const includesForRedHatPattern = /for[^a-z0-9]*red[^a-z0-9]*hat/i;

	const parts = text.split("/") || [];

	for (const part of parts) {
		if (startPattern.test(part)) {
			return false;
		} else if (isSubstringPattern.test(part) && !includesForRedHatPattern.test(part)) {
			return false;
		}
	}

	return true;
};

Yup.addMethod(
	Yup.object,
	"uniqueProperty",
	function (this: Yup.ObjectSchema<any>, propertyName: string, message: string) {
		return this.test("unique", message, function (this: Yup.TestContext, value: any) {
			if (!value?.[propertyName]) {
				return true;
			}
			const { path } = this;
			const options = [...this.parent];
			const currentIndex = options.indexOf(value);

			const subOptions = options.slice(0, currentIndex);

			if (subOptions.some((option: any) => option[propertyName] === value[propertyName])) {
				throw this.createError({
					path: `${path}.${propertyName}`,
					message,
				});
			}
			return true;
		});
	},
);

Yup.addMethod(Yup.string, "unicodeUrl", function (this: Yup.StringSchema, message?: string) {
	return this.test("unicode-url", message || VALID_URL, function (this: Yup.TestContext, value: any) {
		if (!value) return true;
		const { path, createError } = this;
		// Allow empty values if not required
		return UNICODE_URL_REGEX.test(value) || createError({ path, message });
	});
});

// ============================================================================
// INDIVIDUAL FIELD SCHEMAS (reusable across forms and AI import modal)
// ============================================================================

export const nameFieldSchema = requiredString(undefined, PRODUCT_NAME_MAXLENGTH, undefined, true).test(
	"is-product-name-valid",
	RED_HAT_NAMING_GUIDELINE_ERROR,
	isNameValid,
);

export const shortDescriptionFieldSchema = requiredString(
	undefined,
	PRODUCT_GENERAL_SHORT_DESCRIPTION_MAXLENGTH,
	PRODUCT_GENERAL_SHORT_DESCRIPTION_MINLENGTH,
	false,
	true,
);

export const longDescriptionFieldSchema = requiredString(
	undefined,
	PRODUCT_GENERAL_LONG_DESCRIPTION_MAXLENGTH,
	PRODUCT_GENERAL_LONG_DESCRIPTION_MINLENGTH,
);

// General Tab
export const GeneralDetailsValidationObj = {
	[GeneralDetailsFormKeys.name]: nameFieldSchema,
	[GeneralDetailsFormKeys.logo]: Yup.object()
		.shape({
			[LogoFormKeys.url]: Yup.string().url("Must be a valid URL").nullable(),
		})
		.nullable(),
	[GeneralDetailsFormKeys.descriptions]: Yup.object().shape({
		[DescriptionsFormKeys.short]: shortDescriptionFieldSchema,
		[DescriptionsFormKeys.long]: longDescriptionFieldSchema,
	}),
};

// Features and benefits tab
export const featuresFieldSchema = Yup.array()
	.of(
		Yup.object().shape({
			[FeatureItemKeys.title]: Yup.string()
				.max(
					PRODUCT_FEATURES_TITLE_MAXLENGTH,
					REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_FEATURES_TITLE_MAXLENGTH.toString()),
				)
				.test("is-required", REQUIRED_MESSAGE, function (value: any) {
					return !(
						this.parent?.[FeatureItemKeys.description] &&
						this.parent?.[FeatureItemKeys.description].trim() &&
						!value
					);
				}),
			[FeatureItemKeys.description]: Yup.string()
				.max(
					PRODUCT_FEATURES_DESCRIPTION_MAXLENGTH,
					REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_FEATURES_DESCRIPTION_MAXLENGTH.toString()),
				)
				.test("is-required", REQUIRED_MESSAGE, function (value: any) {
					return !(this.parent?.[FeatureItemKeys.title] && this.parent?.[FeatureItemKeys.title].trim() && !value);
				}),
		}),
	)
	.nullable();

export const FeaturesAndBefitsValidationObj = {
	[FeaturesFormKeys.features]: featuresFieldSchema,
};

// Quick start tab
export const quickStartInstructionsFieldSchema = Yup.string()
	.max(
		PRODUCT_QUICK_START_MAXLENGTH,
		REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_QUICK_START_MAXLENGTH.toString()),
	)
	.nullable();

export const QuickStartValidationObj = {
	[QuickStartFormKeys.quick_start_configuration]: Yup.object()
		.shape({
			[QuickStartConfigKeys.instructions]: quickStartInstructionsFieldSchema,
			[QuickStartConfigKeys.default_deploy_instructions]: Yup.boolean(),
		})
		.nullable(),
};

// Linked resources tab
export const linkedResourcesFieldSchema = Yup.array()
	.of(
		Yup.object().shape({
			[LinkedResourceItemKeys.type]: requiredString(),
			[LinkedResourceItemKeys.title]: requiredString(undefined, PRODUCT_LINKED_RESOURCES_TITLE_MAXLENGTH),
			[LinkedResourceItemKeys.description]: requiredString(undefined, PRODUCT_LINKED_RESOURCES_DESCRIPTION_MAXLENGTH),
			[LinkedResourceItemKeys.url]: requiredString(undefined, PRODUCT_LINKED_RESOURCES_URL_MAXLENGTH).unicodeUrl(
				VALID_URL,
			),
		}),
	)
	.required(REQUIRED_MESSAGE);

export const LinkedResourcesDocValidationObj = {
	[LinkedResourcesFormKeys.linked_resources]: linkedResourcesFieldSchema,
};

// FAQ tab
export const faqsFieldSchema = Yup.array()
	.of(
		Yup.object().shape({
			[FAQItemKeys.question]: Yup.string()
				.max(
					PRODUCT_FAQ_QUESTION_MAXLENGTH,
					REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_FAQ_QUESTION_MAXLENGTH.toString()),
				)
				.test("is-required", REQUIRED_MESSAGE, function (value: any) {
					return !(this.parent?.[FAQItemKeys.answer] && this.parent?.[FAQItemKeys.answer].trim() && !value);
				}),
			[FAQItemKeys.answer]: Yup.string()
				.max(
					PRODUCT_FAQ_ANSWER_MAXLENGTH,
					REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_FAQ_ANSWER_MAXLENGTH.toString()),
				)
				.test("is-required", REQUIRED_MESSAGE, function (value: any) {
					return !(this.parent?.[FAQItemKeys.question] && this.parent?.[FAQItemKeys.question].trim() && !value);
				}),
		}),
	)
	.nullable();

export const FAQValidationObj = {
	[FAQFormKeys.faqs]: faqsFieldSchema,
};

// Support tab
export const supportDescriptionFieldSchema = Yup.string()
	.max(PRODUCT_SUPPORT_MAXLENGTH, REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_SUPPORT_MAXLENGTH.toString()))
	.nullable();
export const supportUrlFieldSchema = requiredString().unicodeUrl(VALID_URL);
export const supportPhoneFieldSchema = phoneNumber().nullable();
export const supportEmailFieldSchema = email().nullable();

export const SupportValidationObj = {
	[SupportFormKeys.support]: Yup.object().shape({
		[SupportItemKeys.description]: supportDescriptionFieldSchema,
		[SupportItemKeys.url]: supportUrlFieldSchema,
		[SupportItemKeys.phone_number]: supportPhoneFieldSchema,
		[SupportItemKeys.email_address]: supportEmailFieldSchema,
	}),
};

// Legal Tab
export const PRODUCT_LEGAL_URL_MAXLENGTH = 250;
const MAX_LENGTH_MESSAGE = REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_LEGAL_URL_MAXLENGTH.toString());
export const legalUrlFieldSchema = Yup.string()
	.max(PRODUCT_LEGAL_URL_MAXLENGTH, MAX_LENGTH_MESSAGE)
	.unicodeUrl(VALID_URL)
	.nullable();

export const LegalValidationObj = {
	[LegalFormKeys.legal]: Yup.object()
		.shape({
			[LegalItemKeys.license_agreement_url]: legalUrlFieldSchema,
			[LegalItemKeys.privacy_policy_url]: legalUrlFieldSchema,
		})
		.nullable(),
};

// SEO Tab
export const functionalCategoriesFieldSchema = Yup.array()
	.ensure()
	.min(1, "At least one selection is required")
	.max(3, "Maximum 3 can be specified as category")
	.required("At least one selection is required");

export const searchAliasesFieldSchema = Yup.array().of(
	Yup.object().shape({
		[SearchAliasItemKeys.key]: Yup.string().test("is-required", REQUIRED_MESSAGE, function (value: any) {
			return !(this.parent?.[SearchAliasItemKeys.value] && this.parent?.[SearchAliasItemKeys.value].trim() && !value);
		}),
		[SearchAliasItemKeys.value]: Yup.string().test("is-required", REQUIRED_MESSAGE, function (value: any) {
			return !(this.parent?.[SearchAliasItemKeys.key] && this.parent?.[SearchAliasItemKeys.key].trim() && !value);
		}),
	}),
);

export const SEOValidationObj = {
	[SEOFormKeys.functional_categories]: functionalCategoriesFieldSchema,
	[SEOFormKeys.search_aliases]: searchAliasesFieldSchema,
};

export const contactsFieldSchema = Yup.array()
	.of(
		Yup.object()
			.shape({
				[ContactItemKeys.email_address]: requiredString().email(VALID_EMAIL),
				[ContactItemKeys.type]: requiredString(),
			})
			.test("unique-email-per-type", UNIQUE_EMAIL, function (this: Yup.TestContext, value: any) {
				const currentType = value?.type;
				const currentEmail = value?.email_address;

				if (!currentEmail || !currentType) {
					return true;
				}

				const contactsOfSameType = this?.parent?.filter(
					(contact: any) => contact?.type === currentType && contact?.email_address,
				);

				const emailCount = contactsOfSameType.filter((contact: any) => contact?.email_address === currentEmail).length;

				if (emailCount > 1) {
					return this.createError({
						path: `${this.path}.${ContactItemKeys.email_address}`,
						message: UNIQUE_EMAIL,
					});
				}
				return true;
			})
			.test(
				"needs-contact-types",
				"At least one marketing and technical contact is required",
				function (this: Yup.TestContext) {
					const typeSet = new Set<string>(
						this?.parent?.filter((value: any) => value?.email_address).map((value: any) => value?.type),
					);
					return typeSet.has("Technical contact") && typeSet.has("Marketing contact");
				},
			),
	)
	.required(REQUIRED_MESSAGE)
	.min(2, "At least one marketing and technical contact is required")
	.max(10, MAXIMUM_10_CONTACTS);

export const contactsValidationObject = {
	[ContactsFormKeys.contacts]: contactsFieldSchema,
};

const ProductValidationObj = {
	[ProductFormKeys.id]: Yup.string().required(REQUIRED_MESSAGE),
	...GeneralDetailsValidationObj,
	...FeaturesAndBefitsValidationObj,
	...QuickStartValidationObj,
	...LinkedResourcesDocValidationObj,
	...FAQValidationObj,
	...SupportValidationObj,
	...LegalValidationObj,
	...SEOValidationObj,
	...contactsValidationObject,
};
export const ProductValidationSchema = Yup.object().shape(ProductValidationObj);

interface ErrorState {
	[key: string]: {
		status: boolean;
		nonRequiredFieldCheck?: boolean;
		route: string | null;
		url?: string;
	};
}

export const ValidateRequiredFields = async (
	validationObject: Partial<ProductListing>,
	companyDetails?: ICompanyDetails,
) => {
	const newErrorState: ErrorState = {};
	Object.keys(TabNames).forEach((key) => {
		const tabName = TabNames[key as keyof typeof TabNames];
		const keyName = String(tabName.name);
		Object.assign(newErrorState, {
			[keyName]: {
				status: false,
				nonRequiredFieldCheck: false,
				route: String(tabName.route),
			},
		});
	});
	Object.assign(newErrorState, {
		"Certification Projects": {
			status: false,
			route: "components",
		},
		"Company Details": {
			status: false,
			route: null,
			url: Env.companyProfileURL,
		},
	});
	if (!isEmpty(companyDetails)) {
		try {
			CompanyProfileValidationSchema.validateSync(companyDetails);
			newErrorState["Company Details"].status = false;
		} catch {
			newErrorState["Company Details"].status = true;
		}
	}
	const generalFields = new Set<string>([
		GeneralDetailsFormKeys.name,
		GeneralDetailsFormKeys.logo,
		GeneralDetailsFormKeys.descriptions,
	]);
	const searchFields = new Set<string>([SEOFormKeys.functional_categories, SEOFormKeys.search_aliases]);
	await ProductValidationSchema.validate(validationObject, { abortEarly: false })
		.then()
		.catch((errors: Yup.ValidationError) => {
			errors.inner.forEach((error: Yup.ValidationError) => {
				const path = error.path;
				const fieldName = path?.split(/[.[]/)[0];
				if (fieldName && generalFields.has(fieldName)) {
					newErrorState[TabNames.general.name].status = true;
				} else if (fieldName === FeaturesFormKeys.features) {
					newErrorState[TabNames.features.name].status = true;
				} else if (fieldName === LinkedResourcesFormKeys.linked_resources) {
					newErrorState[TabNames.linkedResources.name].status = true;
				} else if (fieldName === FAQFormKeys.faqs) {
					newErrorState[TabNames.faq.name].status = true;
				} else if (fieldName === SupportFormKeys.support) {
					newErrorState[TabNames.support.name].status = true;
				} else if (fieldName && searchFields.has(fieldName)) {
					newErrorState[TabNames.seo.name].status = true;
				} else if (fieldName === ContactsFormKeys.contacts) {
					newErrorState[TabNames.contacts.name].status = true;
				}
			});
		});
	return { ...newErrorState };
};

export const nonRequiredFields = {
	[GeneralDetailsFormKeys.logo]: Yup.object().shape({
		[LogoFormKeys.url]: Yup.string().url("Must be a valid URL").required(),
	}),
	[QuickStartFormKeys.quick_start_configuration]: Yup.object().shape({
		[QuickStartConfigKeys.instructions]: Yup.string().required(),
	}),
	[SupportFormKeys.support]: Yup.object().shape({
		[SupportItemKeys.description]: Yup.string()
			.max(PRODUCT_SUPPORT_MAXLENGTH, REQUIRED_MAX_CHAR_LENGTH.replace("2048", PRODUCT_SUPPORT_MAXLENGTH.toString()))
			.required(),
		[SupportItemKeys.phone_number]: phoneNumber().required(),
		[SupportItemKeys.email_address]: email().required(),
	}),
	[FAQFormKeys.faqs]: Yup.array().of(
		Yup.object().shape({
			[FAQItemKeys.question]: Yup.string().required(),
			[FAQItemKeys.answer]: Yup.string().required(),
		}),
	),
};

export const nonRequiredFieldsSchema = Yup.object().shape(nonRequiredFields);

export const validateFieldsForIconStatus = async (validationObject: Partial<ProductListing>): Promise<ErrorState> => {
	const errorState = await ValidateRequiredFields(validationObject);
	const generalFields = new Set([GeneralDetailsFormKeys.logo, GeneralDetailsFormKeys.descriptions] as const);
	const searchFields = new Set([SEOFormKeys.functional_categories, SEOFormKeys.search_aliases] as const);
	const newErrorState: ErrorState = { ...cloneDeep(errorState) };
	await nonRequiredFieldsSchema
		.validate(validationObject, { abortEarly: false })
		.then()
		.catch((errors: Yup.ValidationError) => {
			errors.inner.forEach((error: Yup.ValidationError) => {
				const path = error.path;
				const fieldName = path?.split(/[.[]/)[0];
				if (fieldName && (generalFields as Set<string>).has(fieldName)) {
					if (newErrorState[TabNames.general.name]) {
						newErrorState[TabNames.general.name].nonRequiredFieldCheck = true;
					}
				} else if (fieldName === FeaturesFormKeys.features) {
					newErrorState[TabNames.features.name]!.nonRequiredFieldCheck = true;
				} else if (fieldName === QuickStartFormKeys.quick_start_configuration) {
					newErrorState[TabNames.quickStart.name]!.nonRequiredFieldCheck = true;
				} else if (fieldName === LinkedResourcesFormKeys.linked_resources) {
					newErrorState[TabNames.linkedResources.name]!.nonRequiredFieldCheck = true;
				} else if (fieldName === FAQFormKeys.faqs) {
					newErrorState[TabNames.faq.name]!.nonRequiredFieldCheck = true;
				} else if (fieldName === SupportFormKeys.support) {
					newErrorState[TabNames.support.name]!.nonRequiredFieldCheck = true;
				} else if (fieldName === LegalFormKeys.legal) {
					newErrorState[TabNames.legal.name]!.nonRequiredFieldCheck = true;
				} else if (fieldName && (searchFields as Set<string>).has(fieldName)) {
					newErrorState[TabNames.seo.name]!.nonRequiredFieldCheck = true;
				}
			});
		});
	return { ...errorState, ...newErrorState };
};
